<div class="customer-prepaid">
  <!-- What the last cancellation did; the cards below are already refreshed. -->
  <div
    class="customer-prepaid__notice"
    *ngIf="cancelNotice as notice"
    role="status"
    data-testid="cancel-notice"
  >
    <span class="customer-prepaid__notice-title">{{ notice.title }}</span>
    <span>{{ notice.body }}</span>
  </div>

  <p class="customer-prepaid__error" *ngIf="loadError" role="alert">{{ loadError }}</p>

  <p class="customer-prepaid__loading" *ngIf="isLoading" aria-live="polite">
    Refreshing prepaid books...
  </p>

  <ng-container *ngIf="livePurchases.length > 0">
    <h3 class="customer-prepaid__heading" data-testid="active-heading">
      Active prepaids
      <span class="customer-prepaid__count">· {{ livePurchases.length }}</span>
    </h3>
    <ng-container
      *ngFor="let purchase of livePurchases; trackBy: trackByPurchaseId"
      [ngTemplateOutlet]="card"
      [ngTemplateOutletContext]="{ $implicit: purchase }"
    />
  </ng-container>

  <ng-container *ngIf="pastPurchases.length > 0">
    <h3
      class="customer-prepaid__heading customer-prepaid__heading--past"
      data-testid="past-heading"
    >
      Past prepaids
      <span class="customer-prepaid__count">· {{ pastPurchases.length }}</span>
    </h3>
    <ng-container
      *ngFor="let purchase of pastPurchases; trackBy: trackByPurchaseId"
      [ngTemplateOutlet]="card"
      [ngTemplateOutletContext]="{ $implicit: purchase }"
    />
  </ng-container>

  <p class="customer-prepaid__empty" *ngIf="purchases.length === 0 && !isLoading">
    This customer has no prepaid books.
  </p>
</div>

<!--
  The shared cancel & refund panel (CWS-1778), keyed by the book's sign-up order: the
  options endpoint prices the unused washes and CWS-1775 revokes redemption on confirm.
-->
<app-cancel-refund-panel
  *ngIf="cancelPurchase as selected"
  [isOpen]="true"
  [orderId]="selected.order_id"
  [locationId]="locationId"
  intent="cancel"
  [orderNumber]="selected.order_number"
  [customerName]="customerName"
  (closed)="closeCancel()"
  (done)="onCancelDone($event)"
/>

<ng-template #card let-purchase>
  <article
    class="prepaid-card"
    [class.prepaid-card--past]="!purchase.is_live"
    [attr.data-prepaid-purchase-id]="purchase.prepaid_purchase_id"
  >
    <header class="prepaid-card__header">
      <div class="prepaid-card__heading">
        <div class="prepaid-card__title-row">
          <h4 class="prepaid-card__name">{{ purchase.book_name }}</h4>
          <span
            class="prepaid-card__status"
            [class]="'prepaid-card__status prepaid-card__status--' + purchase.display_status"
            data-testid="prepaid-status"
          >
            {{ statusLabel(purchase) }}
          </span>
        </div>
        <!-- Plate chip then the car, as the design shows: "AN7039  Mercedes-Benz". -->
        <p class="prepaid-card__subline" data-testid="prepaid-subline">
          <span class="prepaid-card__plate" *ngIf="plate(purchase) as plate">{{ plate }}</span>
          <span class="prepaid-card__car" *ngIf="vehicleLabel(purchase) as vehicle">{{
            vehicle
          }}</span>
        </p>
      </div>
      <div class="prepaid-card__aside">
        <p class="prepaid-card__price" *ngIf="hasPrice(purchase)">
          <strong>{{ purchase.price | currency: 'USD' }}</strong>
        </p>
        <!-- Row menu: only a live book we sold has anything to offer. Past books get none. -->
        <div class="prepaid-card__menu" *ngIf="hasActions(purchase)" data-prepaid-menu>
          <button
            type="button"
            class="prepaid-card__menu-trigger"
            [class.prepaid-card__menu-trigger--open]="openMenuId === purchase.prepaid_purchase_id"
            [attr.aria-expanded]="openMenuId === purchase.prepaid_purchase_id"
            aria-haspopup="menu"
            aria-label="Prepaid actions"
            data-testid="prepaid-menu-trigger"
            (click)="toggleMenu(purchase)"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" aria-hidden="true">
              <circle cx="9" cy="3.5" r="1.6" />
              <circle cx="9" cy="9" r="1.6" />
              <circle cx="9" cy="14.5" r="1.6" />
            </svg>
          </button>
          <div
            class="prepaid-card__menu-list"
            role="menu"
            *ngIf="openMenuId === purchase.prepaid_purchase_id"
          >
            <button
              type="button"
              class="prepaid-card__menu-item prepaid-card__menu-item--danger"
              role="menuitem"
              data-testid="prepaid-cancel"
              *ngIf="canCancel(purchase)"
              (click)="onCancelPrepaid(purchase)"
            >
              <span class="prepaid-card__menu-title">Cancel prepaid</span>
              <span class="prepaid-card__menu-desc">Remaining washes stop immediately</span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <dl class="prepaid-card__details">
      <div class="prepaid-card__detail">
        <dt>Washes</dt>
        <dd class="prepaid-card__detail-value--strong" data-testid="prepaid-washes">
          {{ washesLeftLabel(purchase) }}
        </dd>
      </div>
      <div class="prepaid-card__detail">
        <dt>Total redeemed</dt>
        <dd class="prepaid-card__detail-value--strong">{{ purchase.redeemed_washes }}</dd>
      </div>
      <div class="prepaid-card__detail">
        <dt>Last washed</dt>
        <dd>{{ (purchase.last_washed_at | date: 'MMM d, y') || '—' }}</dd>
      </div>
      <div class="prepaid-card__detail" data-testid="prepaid-card-on-file">
        <dt>Card on file</dt>
        <dd>{{ cardOnFileLabel(purchase) }}</dd>
      </div>
      <div class="prepaid-card__detail" data-testid="prepaid-washes-names">
        <dt>Wash names</dt>
        <!-- One chip: the first wash, an ellipsis when there are more; the tooltip has them all. -->
        <dd>
          <span class="prepaid-card__chip" [title]="washNamesLabel(purchase)">{{
            washNamesBadge(purchase)
          }}</span>
        </dd>
      </div>
      <div class="prepaid-card__detail" data-testid="prepaid-source">
        <dt>Source</dt>
        <dd>{{ sourceLabel(purchase) }}</dd>
      </div>
      <div class="prepaid-card__detail" data-testid="prepaid-expires">
        <dt>Expires</dt>
        <dd>{{ (purchase.expires_at | date: 'MMM d, y') || 'Never' }}</dd>
      </div>
      <div class="prepaid-card__detail">
        <dt>Purchased on</dt>
        <dd>{{ (purchase.purchased_at | date: 'MMM d, y') || '—' }}</dd>
      </div>
      <!-- CWS-1908: what the customer reads out at the lane when the QR cannot be scanned. -->
      <div class="prepaid-card__detail" data-testid="prepaid-code">
        <dt>Redemption code</dt>
        <dd class="prepaid-card__detail-value--strong">{{ purchase.redemption_code || '—' }}</dd>
      </div>
    </dl>

    <!-- CWS-1881: who bought the book. Stays the buyer however many relatives redeem from it. -->
    <section class="prepaid-card__purchaser" data-testid="prepaid-purchaser">
      <h5 class="prepaid-card__section-title">Purchased by</h5>
      <dl class="prepaid-card__purchaser-grid" *ngIf="purchaserCells(purchase) as cells">
        <div class="prepaid-card__detail">
          <dt>Name</dt>
          <dd [class.prepaid-card__muted]="cells.name.muted" data-testid="purchaser-name">
            {{ cells.name.text }}
          </dd>
        </div>
        <div class="prepaid-card__detail">
          <dt>Phone</dt>
          <dd [class.prepaid-card__muted]="cells.phone.muted" data-testid="purchaser-phone">
            {{ cells.phone.text }}
          </dd>
        </div>
        <div class="prepaid-card__detail">
          <dt>Email</dt>
          <dd [class.prepaid-card__muted]="cells.email.muted" data-testid="purchaser-email">
            {{ cells.email.text }}
          </dd>
        </div>
        <div class="prepaid-card__detail">
          <dt>Plate</dt>
          <dd data-testid="purchaser-plate">
            <span class="prepaid-card__plate" [class.prepaid-card__muted]="cells.plate.muted">{{
              cells.plate.text
            }}</span>
          </dd>
        </div>
      </dl>
    </section>

    <!--
      CWS-1881: every wash drawn off the book, with whoever took it. Collapsed by default, as
      the design shows; the count is one the card already has, so the title reads right before
      the log has ever been fetched.
    -->
    <ng-container *ngIf="logState(purchase) as log">
      <button
        type="button"
        class="prepaid-card__log-toggle"
        [attr.aria-expanded]="log.open"
        [attr.aria-controls]="'prepaid-log-' + purchase.prepaid_purchase_id"
        data-testid="prepaid-log-toggle"
        (click)="toggleLog(purchase)"
      >
        <svg
          class="prepaid-card__log-chevron"
          [class.prepaid-card__log-chevron--open]="log.open"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="m9 6 6 6-6 6" />
        </svg>
        <span>{{ logTitle(purchase) }}</span>
      </button>

      <div
        class="prepaid-card__log"
        *ngIf="log.open"
        [id]="'prepaid-log-' + purchase.prepaid_purchase_id"
        data-testid="prepaid-log"
      >
        <p class="prepaid-card__log-state" *ngIf="log.loading" aria-live="polite">
          Loading redemption history...
        </p>

        <p
          class="prepaid-card__log-state prepaid-card__log-state--error"
          *ngIf="log.error"
          role="alert"
        >
          {{ log.error }}
          <button type="button" class="prepaid-card__log-retry" (click)="retryLog(purchase)">
            Try again
          </button>
        </p>

        <ng-container *ngIf="!log.loading && !log.error && log.rows !== null">
          <p
            class="prepaid-card__log-state"
            *ngIf="log.rows.length === 0"
            data-testid="prepaid-log-empty"
          >
            No washes have been redeemed from this book yet.
          </p>

          <div class="prepaid-card__log-table-wrap" *ngIf="log.rows.length > 0">
            <table class="prepaid-card__log-table" data-testid="prepaid-log-table">
              <thead>
                <tr>
                  <th scope="col">Date &amp; time</th>
                  <th scope="col">Plate</th>
                  <th scope="col">Name</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Email</th>
                  <th scope="col" class="prepaid-card__log-th--customer">Customer</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let row of logRows(purchase); trackBy: trackByRowId"
                  data-testid="prepaid-log-row"
                >
                  <td class="prepaid-card__log-when">
                    <span>{{ row.date }}</span>
                    <span class="prepaid-card__log-time">{{ row.time }}</span>
                  </td>
                  <td>
                    <span
                      class="prepaid-card__plate"
                      [class.prepaid-card__muted]="row.plate.muted"
                      >{{ row.plate.text }}</span
                    >
                  </td>
                  <td class="prepaid-card__log-name">
                    <span [class.prepaid-card__muted]="row.name.muted">{{ row.name.text }}</span>
                    <span class="prepaid-card__log-note" *ngIf="row.note">{{ row.note }}</span>
                  </td>
                  <td [class.prepaid-card__muted]="row.phone.muted">{{ row.phone.text }}</td>
                  <td class="prepaid-card__log-email" [class.prepaid-card__muted]="row.email.muted">
                    {{ row.email.text }}
                  </td>
                  <td class="prepaid-card__log-customer">
                    <!-- Only a row with a customer record has somewhere to go. -->
                    <button
                      *ngIf="row.customerId"
                      type="button"
                      class="prepaid-card__log-link"
                      title="View customer"
                      aria-label="View customer"
                      data-testid="prepaid-log-view-customer"
                      (click)="onViewCustomer(row)"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M15 3h6v6" />
                        <path d="M10 14 21 3" />
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p class="prepaid-card__log-help" data-testid="prepaid-log-help">{{ logHelp }}</p>
        </ng-container>
      </div>
    </ng-container>
  </article>
</ng-template>
